import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLeaf, FaTractor, FaHeart, FaArrowLeft } from 'react-icons/fa';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-cyan-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-10">
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-8 text-white">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-4 md:mb-0">
                <h1 className="text-4xl font-bold mb-2">About KisanFresh</h1>
                <p className="text-green-100 text-xl max-w-2xl">Bringing fresh produce directly from farms to your doorstep.</p>
              </div>
              <button 
                onClick={() => navigate('/')} 
                className="flex items-center gap-2 bg-white text-green-700 hover:bg-green-50 transition-colors duration-300 px-6 py-3 rounded-full font-medium shadow-md"
              >
                <FaArrowLeft /> Back to Home
              </button>
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-6 px-8 pb-8">
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-5 rounded-xl text-center shadow-sm">
              <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl mx-auto mb-3">
                <FaTractor />
              </div>
              <h3 className="font-bold text-gray-800 text-lg">100+ Farmers</h3>
              <p className="text-gray-600 text-sm mt-1">Connected nationwide</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-5 rounded-xl text-center shadow-sm">
              <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl mx-auto mb-3">
                <FaLeaf />
              </div>
              <h3 className="font-bold text-gray-800 text-lg">Organic Produce</h3>
              <p className="text-gray-600 text-sm mt-1">Quality guaranteed</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 p-5 rounded-xl text-center shadow-sm">
              <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl mx-auto mb-3">
                <FaHeart />
              </div>
              <h3 className="font-bold text-gray-800 text-lg">Eco-Friendly</h3>
              <p className="text-gray-600 text-sm mt-1">Sustainable farming</p>
            </div>
          </div>
        </div>

        {/* Mission & What We Do Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-700 font-bold">M</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
            </div>
            <p className="text-gray-700 leading-relaxed text-lg">
              We empower local farmers by providing a reliable marketplace that connects them with customers who value fresh, sustainable produce. Our goal is to shorten the supply chain, reduce waste, and make healthy food affordable.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-700 font-bold">W</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">What We Do</h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-700 text-xs">✓</span>
                </div>
                <span className="text-gray-700 text-lg">Source fresh produce from verified farms</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-700 text-xs">✓</span>
                </div>
                <span className="text-gray-700 text-lg">Package and deliver with minimal handling</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-green-700 text-xs">✓</span>
                </div>
                <span className="text-gray-700 text-lg">Offer transparent pricing and farmer support</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <section className="mb-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Why Choose Us?</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-600 mx-auto rounded"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 rounded-xl bg-green-100 flex items-center justify-center text-green-700 text-2xl mb-5">
                <FaTractor />
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3">Direct Farm Connection</h4>
              <p className="text-gray-600">Connect directly with local farmers and cut out unnecessary middlemen, ensuring freshness and fair prices.</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl p-6 shadow-lg border border-emerald-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 text-2xl mb-5">
                <FaLeaf />
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3">Fresh & Organic</h4>
              <p className="text-gray-600">Only the freshest organic produce delivered to your doorstep, harvested at peak ripeness.</p>
            </div>
            
            <div className="bg-gradient-to-br from-white to-teal-50 rounded-2xl p-6 shadow-lg border border-teal-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 rounded-xl bg-teal-100 flex items-center justify-center text-teal-700 text-2xl mb-5">
                <FaHeart />
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-3">Farmer Support</h4>
              <p className="text-gray-600">We ensure fair compensation for farmers and promote sustainable agricultural practices.</p>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white mb-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-1 bg-green-300 rounded mx-auto mb-6"></div>
            <blockquote className="text-xl italic mb-6">
              "KisanFresh has transformed how we connect with our customers. The platform ensures our organic produce reaches consumers in the best possible condition."
            </blockquote>
            <div className="font-bold text-green-200">- Local Farmer Cooperative</div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;